/**
 * @fileoverview Toast notification component and container.
 *
 * Displays stackable toast notifications with support for success, error,
 * and info message types. Each toast auto-dismisses after 5 seconds and
 * includes a manual dismiss button.
 */

import { useToast, type Toast as ToastData, type ToastType } from '../contexts/ToastContext';

/**
 * Icon components for each toast type.
 */
const ICONS: Record<ToastType, JSX.Element> = {
  success: (
    <svg
      className="h-5 w-5 text-green-400"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg
      className="h-5 w-5 text-red-400"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
        clipRule="evenodd"
      />
    </svg>
  ),
  info: (
    <svg
      className="h-5 w-5 text-blue-400"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

/**
 * Background color classes for each toast type.
 */
const BG_COLORS: Record<ToastType, string> = {
  success: 'bg-green-500/10 border-green-500/20',
  error: 'bg-red-500/10 border-red-500/20',
  info: 'bg-blue-500/10 border-blue-500/20',
};

/**
 * Text color classes for toast title.
 */
const TITLE_COLORS: Record<ToastType, string> = {
  success: 'text-green-400',
  error: 'text-red-400',
  info: 'text-blue-400',
};

/**
 * Props for individual Toast component.
 */
interface ToastItemProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

/**
 * Individual toast notification item.
 *
 * @param props - Component props
 * @param props.toast
 * @param props.onDismiss
 * @returns Toast notification element
 */
function ToastItem({ toast, onDismiss }: ToastItemProps): JSX.Element {
  return (
    <div
      className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg ${BG_COLORS[toast.type]}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">{ICONS[toast.type]}</div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            {toast.title && (
              <p className={`text-sm font-medium ${TITLE_COLORS[toast.type]}`}>
                {toast.title}
              </p>
            )}
            <p className={`text-sm text-slate-300 ${toast.title ? 'mt-1' : ''}`}>
              {toast.message}
            </p>
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              type="button"
              className="inline-flex rounded-md text-slate-400 hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              onClick={() => onDismiss(toast.id)}
              aria-label="Dismiss notification"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Toast container component.
 *
 * Renders a stack of toast notifications in the top-right corner of the viewport.
 * Toasts are ordered with newest at the bottom and animate into view.
 *
 * Place this component once at the app root level, typically alongside providers.
 *
 * @returns Toast container element
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <ToastProvider>
 *   <Routes />
 *   <ToastContainer />
 * </ToastProvider>
 * ```
 */
export function ToastContainer(): JSX.Element {
  const { toasts, removeToast } = useToast();

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-0 z-50 flex flex-col items-end justify-start gap-2 p-4 sm:p-6"
    >
      <div className="flex w-full flex-col items-end gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </div>
    </div>
  );
}
